/**
 * @fileoverview Workflow editor (MOR-696, renamed MOR-716)
 *
 * Full-page editor for a single workflow: a settings card (name,
 * description, apply mode, appointment types, anchor) plus an ordered list of
 * editable task cards (status, priority, labels, assignee/assigned-by, parent,
 * blocked-by, relative offsets). Each row can spawn a nested subtask directly.
 * Edits live in local state and autosave on a
 * short debounce, on blur, and on unmount. Rows can be ticked (or Select All)
 * for bulk actions via a floating bottom bar — Edit (dialog applying status /
 * priority / assignee / assigned-by / labels / due offset, plus the structural
 * fields: parent task, blocked by, blocks — MOR-804), Duplicate, and Delete —
 * matching the precedent templates gallery pattern.
 *
 * Save model: blank (untitled) task rows are not persisted, and parent /
 * blocked-by references to unsaved rows are stripped, so the backend's
 * non-empty-title + reference-integrity validation in `replaceItems` never
 * trips while the user is mid-edit.
 *
 * Used by: src/routes/.../_layout.workflows.$workflowId.tsx
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ArrowLeft,
  Ban,
  CalendarClock,
  CheckSquare,
  CircleDot,
  Copy,
  CornerDownRight,
  Flag,
  Link2,
  List,
  Loader2,
  Pencil,
  Plus,
  Search,
  Tags,
  Trash2,
  User,
  UserCheck,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import { Button, Checkbox, Input, Label, Textarea } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { cn } from "@/utils/misc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { Tabs, TabsList, TabsTrigger } from "@/ui/composed/sections/tabs";
import { rulesTargetingTaskTitle } from "@/components/settings/automations/automation-config";
import { useAutosave, AutosaveStatus } from "@/utils/forms";
import { APPOINTMENT_TYPE_TAGS } from "@/lib/precedent-templates";
import { TASK_PRIORITIES } from "@/calendar/task-priority";
import { toast } from "sonner";
import {
  WorkflowItemCard,
  type DraftItem,
  type TaskAutomationRule,
} from "./WorkflowItemCard";
import {
  applyBulkEditToItems,
  invalidParentKeys,
  type BulkEditChange,
} from "./workflowBulkEdit";
import { computeReorderedItems } from "./workflowStepModel";
import { WorkflowFlowView } from "./WorkflowFlowView";
import { WorkflowAutomationsSection } from "@/components/settings/automations/WorkflowAutomationsSection";
import {
  MemberOrRoleSelect,
  type MemberOrRoleValue,
} from "./MemberOrRoleSelect";

const FILTER_ALL = "__all__";
const FILTER_NO_STATUS = "__no_status__";
const BULK_NONE = "__none__";

type ApplyMode = "manual" | "all_jobs" | "by_type";
type Anchor = "appointment_date" | "apply_date";
type EditorTab = "tasks" | "automations";

interface HeaderDraft {
  name: string;
  description: string;
  applyMode: ApplyMode;
  appointmentTypes: string[];
  anchor: Anchor;
}

const APPLY_MODE_OPTIONS: ReadonlyArray<{ value: ApplyMode; label: string }> = [
  { value: "manual", label: "Manual — apply on demand" },
  { value: "all_jobs", label: "Automatic — every new job" },
  { value: "by_type", label: "Automatic — by appointment type" },
];

const ANCHOR_OPTIONS: ReadonlyArray<{ value: Anchor; label: string }> = [
  { value: "appointment_date", label: "Job appointment date" },
  { value: "apply_date", label: "Date the workflow is applied" },
];

let keyCounter = 0;
function newLocalKey(): string {
  keyCounter += 1;
  return `k_${Date.now().toString(36)}_${keyCounter}`;
}

function emptyItem(): DraftItem {
  return {
    localKey: newLocalKey(),
    title: "",
    description: "",
    priority: null,
    statusKey: null,
    labelKeys: [],
    assigneeUserId: null,
    assignorUserId: null,
    assigneeRoleId: null,
    assignorRoleId: null,
    parentLocalKey: null,
    blockedByKeys: [],
    dueOffsetDays: null,
    dueOffsetUnit: null,
    plannedOffsetDays: null,
    plannedOffsetUnit: null,
    plannedDurationDays: null,
    recurrence: null,
  };
}

/**
 * Order tasks so every subtask sits directly beneath its parent (depth-first),
 * preserving the existing relative order of roots and of siblings. Subtasks
 * whose parent is missing are treated as top-level, and a parent cycle can
 * never drop or duplicate a row.
 */
function groupSubtasksUnderParents(items: DraftItem[]): DraftItem[] {
  const keySet = new Set(items.map((it) => it.localKey));
  const roots: DraftItem[] = [];
  const childrenOf = new Map<string, DraftItem[]>();
  for (const it of items) {
    const parent =
      it.parentLocalKey && keySet.has(it.parentLocalKey)
        ? it.parentLocalKey
        : null;
    if (parent === null) {
      roots.push(it);
    } else {
      const arr = childrenOf.get(parent) ?? [];
      arr.push(it);
      childrenOf.set(parent, arr);
    }
  }
  const result: DraftItem[] = [];
  const seen = new Set<string>();
  const visit = (it: DraftItem) => {
    if (seen.has(it.localKey)) return;
    seen.add(it.localKey);
    result.push(it);
    for (const child of childrenOf.get(it.localKey) ?? []) visit(child);
  };
  for (const root of roots) visit(root);
  // Safety net: keep any row skipped by a parent cycle rather than losing it.
  for (const it of items) if (!seen.has(it.localKey)) result.push(it);
  return result;
}

/**
 * Depth of each item in the parent/child tree (roots = 0), used to indent
 * nested subtasks one step per level. Missing parents are treated as
 * top-level and cycles are cut rather than recursing forever, mirroring
 * `groupSubtasksUnderParents`.
 */
function computeItemDepths(items: DraftItem[]): Map<string, number> {
  const keySet = new Set(items.map((it) => it.localKey));
  const parentOf = new Map<string, string | null>();
  for (const it of items) {
    parentOf.set(
      it.localKey,
      it.parentLocalKey && keySet.has(it.parentLocalKey)
        ? it.parentLocalKey
        : null,
    );
  }
  const depths = new Map<string, number>();
  const resolve = (key: string, trail: Set<string>): number => {
    const cached = depths.get(key);
    if (cached !== undefined) return cached;
    const parent = parentOf.get(key) ?? null;
    const depth =
      parent !== null && !trail.has(parent)
        ? (() => {
            trail.add(key);
            return resolve(parent, trail) + 1;
          })()
        : 0;
    depths.set(key, depth);
    return depth;
  };
  for (const it of items) resolve(it.localKey, new Set([it.localKey]));
  return depths;
}

/**
 * Wraps a `WorkflowItemCard` with `useSortable`, forwarding the node ref +
 * transform and the handle activator. Handle-only dragging (the grip) keeps the
 * inline inputs fully usable. Disabled (no drag) when `canDrag` is false.
 */
function SortableTemplateItem({
  orgId,
  item,
  index,
  depth,
  statuses,
  labels,
  otherItems,
  disabled,
  canDrag,
  selected,
  onSelectedChange,
  onChange,
  onRemove,
  onAddSubtask,
  autoFocusTitle,
  automationRules,
  templateId,
  templateName,
  workflowTaskTitles,
}: {
  orgId: Id<"organizations">;
  item: DraftItem;
  index: number;
  depth: number;
  statuses: ReadonlyArray<{ key: string; name: string; color?: string }>;
  labels: ReadonlyArray<{ key: string; name: string }>;
  otherItems: ReadonlyArray<{ localKey: string; title: string }>;
  disabled: boolean;
  canDrag: boolean;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onChange: (patch: Partial<DraftItem>) => void;
  onRemove: () => void;
  onAddSubtask?: () => void;
  autoFocusTitle?: boolean;
  automationRules?: ReadonlyArray<TaskAutomationRule>;
  templateId: Id<"taskTemplates">;
  templateName: string;
  workflowTaskTitles: ReadonlyArray<string>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.localKey, disabled: !canDrag });

  return (
    <WorkflowItemCard
      orgId={orgId}
      item={item}
      index={index}
      depth={depth}
      statuses={statuses}
      labels={labels}
      otherItems={otherItems}
      disabled={disabled}
      selected={selected}
      onSelectedChange={onSelectedChange}
      onChange={onChange}
      onRemove={onRemove}
      onAddSubtask={onAddSubtask}
      autoFocusTitle={autoFocusTitle}
      automationRules={automationRules}
      templateId={templateId}
      templateName={templateName}
      workflowTaskTitles={workflowTaskTitles}
      sortable={{
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        style: {
          transform: CSS.Transform.toString(transform),
          transition,
        },
        isDragging,
        canDrag,
      }}
    />
  );
}

export function WorkflowEditor({
  orgId,
  templateId,
  canEdit,
  initialTab,
}: {
  orgId: Id<"organizations">;
  templateId: Id<"taskTemplates">;
  canEdit: boolean;
  /** Deep-link target: open on the automations tab (?tab=automations). */
  initialTab?: EditorTab;
}) {
  const navigate = useNavigate();
  const { data, isPending } = useQuery(
    convexQuery(api.tasks.workflows.getTemplate, { templateId }),
  );
  const { data: statuses } = useQuery(
    convexQuery(api.tasks.statuses.listByOrg, { orgId }),
  );
  const { data: labels } = useQuery(
    convexQuery(api.tasks.labels.listByOrg, { orgId }),
  );
  // Rule count for the Automations tab label (shares the section's cache).
  const { data: templateRules } = useQuery(
    convexQuery(api.automations.rules.listByTemplate, { templateId }),
  );

  const updateTemplate = useConvexMutation(api.tasks.workflows.update);
  const replaceItems = useConvexMutation(api.tasks.workflows.replaceItems);

  const [header, setHeader] = useState<HeaderDraft | null>(null);
  const [items, setItems] = useState<DraftItem[]>([]);
  // The task added most recently, so its title field takes focus on mount.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const hydratedRef = useRef(false);

  // --- search + filters ----------------------------------------------------
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(FILTER_ALL);
  const [priorityFilter, setPriorityFilter] = useState<string>(FILTER_ALL);

  // --- bulk-edit selection ---------------------------------------------------
  // Mirrors the precedents gallery: selectionMode keeps the floating action
  // bar visible even at 0 selected; leaving select-mode drops the selection.
  // List ⇄ visual graph toggle (MOR-736). The flow view renders the same
  // draft items, so edits made there autosave through the same pipeline.
  const [viewMode, setViewMode] = useState<"list" | "flow">("list");
  // Tasks ⇄ Automations tab (MOR-736).
  const [activeTab, setActiveTab] = useState<EditorTab>(initialTab ?? "tasks");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Hydrate local state once the workflow loads.
  useEffect(() => {
    if (!data || hydratedRef.current) return;
    hydratedRef.current = true;
    setHeader({
      name: data.template.name,
      description: data.template.description ?? "",
      applyMode: data.template.applyMode,
      appointmentTypes: data.template.appointmentTypes,
      anchor: data.template.anchor,
    });
    setItems(
      data.items.map((it) => ({
        localKey: it.localKey,
        title: it.title,
        description: it.description ?? "",
        priority: it.priority,
        statusKey: it.statusKey,
        labelKeys: it.labelKeys,
        assigneeUserId: it.assigneeUserId,
        assignorUserId: it.assignorUserId,
        assigneeRoleId: it.assigneeRoleId,
        assignorRoleId: it.assignorRoleId,
        parentLocalKey: it.parentLocalKey,
        blockedByKeys: it.blockedByKeys,
        dueOffsetDays: it.dueOffsetDays,
        dueOffsetUnit: it.dueOffsetUnit,
        plannedOffsetDays: it.plannedOffsetDays,
        plannedOffsetUnit: it.plannedOffsetUnit,
        plannedDurationDays: it.plannedDurationDays,
        recurrence: it.recurrence
          ? {
              frequency: it.recurrence.frequency,
              interval: it.recurrence.interval,
              count: it.recurrence.count ?? null,
            }
          : null,
      })),
    );
  }, [data]);

  // Keep subtasks grouped directly beneath their parent in the list order.
  // Runs whenever the task structure changes; a no-op (already grouped) skips
  // setState so it never loops or triggers a spurious autosave.
  useEffect(() => {
    const grouped = groupSubtasksUnderParents(items);
    const unchanged =
      grouped.length === items.length &&
      grouped.every((it, i) => it.localKey === items[i].localKey);
    if (!unchanged) setItems(grouped);
  }, [items]);

  // --- autosave ------------------------------------------------------------
  const getValue = useCallback(() => ({ header, items }), [header, items]);

  const save = useCallback(
    async (value: { header: HeaderDraft | null; items: DraftItem[] }) => {
      if (!value.header) return;
      await updateTemplate({
        templateId,
        patch: {
          name: value.header.name.trim() || "Untitled workflow",
          description: value.header.description.trim() || null,
          applyMode: value.header.applyMode,
          appointmentTypes:
            value.header.applyMode === "by_type"
              ? (value.header.appointmentTypes as Parameters<
                  typeof updateTemplate
                >[0]["patch"]["appointmentTypes"])
              : [],
          anchor: value.header.anchor,
        },
      });

      // Only persist titled rows; strip references to dropped rows.
      const titled = value.items.filter((it) => it.title.trim().length > 0);
      const validKeys = new Set(titled.map((it) => it.localKey));
      await replaceItems({
        templateId,
        items: titled.map((it) => ({
          localKey: it.localKey,
          title: it.title,
          description: it.description.trim() || undefined,
          priority: it.priority ?? undefined,
          statusKey: it.statusKey ?? undefined,
          labelKeys: it.labelKeys.length > 0 ? it.labelKeys : undefined,
          assigneeUserId: it.assigneeUserId ?? undefined,
          assignorUserId: it.assignorUserId ?? undefined,
          assigneeRoleId: it.assigneeRoleId ?? undefined,
          assignorRoleId: it.assignorRoleId ?? undefined,
          parentLocalKey:
            it.parentLocalKey && validKeys.has(it.parentLocalKey)
              ? it.parentLocalKey
              : undefined,
          blockedByKeys: it.blockedByKeys.filter((k) => validKeys.has(k)),
          dueOffsetDays: it.dueOffsetDays ?? undefined,
          dueOffsetUnit: it.dueOffsetUnit ?? undefined,
          plannedOffsetDays: it.plannedOffsetDays ?? undefined,
          plannedOffsetUnit: it.plannedOffsetUnit ?? undefined,
          plannedDurationDays: it.plannedDurationDays ?? undefined,
          recurrence:
            // Subtasks / blocked tasks can't recur (enforced server-side too).
            it.recurrence && !it.parentLocalKey && it.blockedByKeys.length === 0
              ? {
                  frequency: it.recurrence.frequency,
                  interval: Math.max(1, it.recurrence.interval),
                  count: it.recurrence.count ?? undefined,
                }
              : undefined,
        })),
      });
    },
    [templateId, updateTemplate, replaceItems],
  );

  const autosave = useAutosave({
    getValue,
    save,
    enabled: canEdit && header !== null,
  });
  const { flush, resetBaseline, status } = autosave;

  // Establish the saved baseline once hydrated so we don't save on load.
  // Intentionally excludes header/items: re-running on every edit would reset
  // the baseline and the debounced autosave would never detect a change.
  const hydrated = header !== null;
  useEffect(() => {
    if (hydrated) resetBaseline({ header, items });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- header/items deliberately excluded: this must fire exactly once, at the moment `hydrated` flips true, to snapshot the just-loaded data as the autosave baseline. Depending on header/items would re-run on every edit and reset the baseline to the current (edited) value, so the debounced autosave below would never see a diff to save. */
  }, [hydrated, resetBaseline]);

  // Debounced autosave on any change.
  useEffect(() => {
    if (!canEdit || header === null) return;
    const t = setTimeout(() => {
      void flush().catch(() => {
        toast.error("Couldn't save the workflow. Your edits are kept locally.");
      });
    }, 800);
    return () => clearTimeout(t);
  }, [header, items, canEdit, flush]);

  // Flush pending edits when leaving the editor (unmount only). Keep the
  // latest flush in a ref so this effect doesn't re-run (and flush early) on
  // every edit — that would defeat the debounce above.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    return () => {
      void flushRef.current();
    };
  }, []);

  const patchItem = (localKey: string, patch: Partial<DraftItem>) =>
    setItems((prev) =>
      prev.map((it) => (it.localKey === localKey ? { ...it, ...patch } : it)),
    );

  const removeItem = (localKey: string) => {
    setItems((prev) =>
      prev
        // Drop the row, and detach any children/dependents pointing at it.
        .filter((it) => it.localKey !== localKey)
        .map((it) => ({
          ...it,
          parentLocalKey:
            it.parentLocalKey === localKey ? null : it.parentLocalKey,
          blockedByKeys: it.blockedByKeys.filter((k) => k !== localKey),
        })),
    );
    setSelectedKeys((prev) => {
      if (!prev.has(localKey)) return prev;
      const next = new Set(prev);
      next.delete(localKey);
      return next;
    });
  };

  const toggleSelected = (localKey: string, selected: boolean) => {
    setSelectionMode(true);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (selected) next.add(localKey);
      else next.delete(localKey);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedKeys(new Set());
  };

  /**
   * Apply a bulk edit to every selected task; autosave persists it. Structural
   * edits (MOR-804) reach beyond the selection: "Blocks" adds each selected key
   * to the blockers of the tasks it names, so those rows change too.
   */
  const applyBulkEdit = (change: BulkEditChange) => {
    const n = selectedKeys.size;
    setItems((prev) => applyBulkEditToItems(prev, selectedKeys, change));
    setBulkEditOpen(false);
    exitSelectionMode();
    toast.success(`Updated ${n} task${n === 1 ? "" : "s"}.`);
  };

  /** Append copies of the selected tasks, remapping intra-selection links. */
  const bulkDuplicate = () => {
    const n = selectedKeys.size;
    if (n === 0) return;
    setItems((prev) => {
      // New key per selected row; parent/blocked-by links between two
      // selected rows point at the copies, links to unselected rows are kept.
      const keyMap = new Map<string, string>();
      for (const it of prev) {
        if (selectedKeys.has(it.localKey)) {
          keyMap.set(it.localKey, newLocalKey());
        }
      }
      const copies = prev
        .filter((it) => keyMap.has(it.localKey))
        .map((it) => ({
          ...it,
          localKey: keyMap.get(it.localKey) as string,
          title: it.title.trim() ? `${it.title} (Copy)` : it.title,
          parentLocalKey: it.parentLocalKey
            ? (keyMap.get(it.parentLocalKey) ?? it.parentLocalKey)
            : null,
          blockedByKeys: it.blockedByKeys.map((k) => keyMap.get(k) ?? k),
        }));
      return [...prev, ...copies];
    });
    exitSelectionMode();
    toast.success(`Duplicated ${n} task${n === 1 ? "" : "s"}.`);
  };

  const bulkDelete = () => {
    const n = selectedKeys.size;
    setItems((prev) =>
      prev
        .filter((it) => !selectedKeys.has(it.localKey))
        .map((it) => ({
          ...it,
          parentLocalKey:
            it.parentLocalKey && selectedKeys.has(it.parentLocalKey)
              ? null
              : it.parentLocalKey,
          blockedByKeys: it.blockedByKeys.filter((k) => !selectedKeys.has(k)),
        })),
    );
    setBulkDeleteOpen(false);
    exitSelectionMode();
    toast.success(`Deleted ${n} task${n === 1 ? "" : "s"}.`);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter(FILTER_ALL);
    setPriorityFilter(FILTER_ALL);
  };

  const addItem = () => {
    // Clear filters so the freshly added (empty-title) card is visible.
    clearFilters();
    const item = emptyItem();
    setFocusKey(item.localKey);
    setItems((prev) => [...prev, item]);
  };

  /**
   * Add a task nested under `parentLocalKey`, so a subtask can be created from
   * the parent row instead of adding a top-level task and re-parenting it. The
   * row is inserted after the parent's existing descendants — the list is
   * already depth-first, so a parent's subtree is contiguous — which lands the
   * new task at the end of that subtree and leaves the grouping pass a no-op.
   */
  const addSubtask = (parentLocalKey: string) => {
    clearFilters();
    const child = { ...emptyItem(), parentLocalKey };
    setFocusKey(child.localKey);
    setItems((prev) => {
      const parentIndex = prev.findIndex(
        (it) => it.localKey === parentLocalKey,
      );
      if (parentIndex < 0) return [...prev, child];
      const subtree = new Set([parentLocalKey]);
      let end = parentIndex + 1;
      while (end < prev.length) {
        const parent = prev[end].parentLocalKey;
        if (parent === null || !subtree.has(parent)) break;
        subtree.add(prev[end].localKey);
        end += 1;
      }
      return [...prev.slice(0, end), child, ...prev.slice(end)];
    });
  };

  // --- drag-to-reorder -----------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleReorder = (e: DragEndEvent) => {
    const { active, over } = e;
    setItems((prev) => {
      const reordered = computeReorderedItems(
        prev,
        active.id as string,
        over?.id as string | undefined,
      );
      return reordered ?? prev;
    });
  };

  const statusOptions = useMemo(
    () =>
      (statuses ?? []).map((s) => ({
        key: s.key,
        name: s.name,
        color: s.color,
      })),
    [statuses],
  );
  const labelOptions = useMemo(
    () => (labels ?? []).map((l) => ({ key: l.key, name: l.name })),
    [labels],
  );
  const otherItems = useMemo(
    () => items.map((it) => ({ localKey: it.localKey, title: it.title })),
    [items],
  );
  const workflowTaskTitles = useMemo(
    () => items.map((it) => it.title),
    [items],
  );

  // Which of this workflow's automation rules target each task (MOR-736) —
  // shown as ⚡ chips on the rows and a badge recap block in the expanded
  // editor. The full rule objects are threaded through so the recap can render
  // coloured trigger/action badges (matching the workflow automations list).
  const automationRulesByKey = useMemo(() => {
    const map = new Map<string, TaskAutomationRule[]>();
    if (!templateRules || templateRules.length === 0) return map;
    for (const it of items) {
      const matched = rulesTargetingTaskTitle(templateRules, it.title);
      if (matched.length > 0) map.set(it.localKey, matched);
    }
    return map;
  }, [templateRules, items]);

  const isFiltering =
    search.trim() !== "" ||
    statusFilter !== FILTER_ALL ||
    priorityFilter !== FILTER_ALL;

  // Nesting depth per item so subtasks of subtasks indent one step per level.
  const depthByKey = useMemo(() => computeItemDepths(items), [items]);

  // Keep original indices so card numbering + edits stay stable while filtered.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => {
        if (q) {
          const hay = `${it.title} ${it.description}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (statusFilter !== FILTER_ALL) {
          if (statusFilter === FILTER_NO_STATUS) {
            if (it.statusKey !== null) return false;
          } else if (it.statusKey !== statusFilter) {
            return false;
          }
        }
        if (priorityFilter !== FILTER_ALL) {
          if (String(it.priority ?? 0) !== priorityFilter) return false;
        }
        return true;
      });
  }, [items, search, statusFilter, priorityFilter]);

  // "Select All" picks up every currently visible (filtered) row.
  const enterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedKeys(new Set(visibleItems.map(({ it }) => it.localKey)));
  };

  const goBack = () => navigate({ to: "/dashboard/workflows" });

  if (isPending || header === null) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading workflow…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4 py-12 text-center">
        <p className="text-sm text-muted-foreground">
          This workflow no longer exists.
        </p>
        <Button variant="outline" onClick={() => void goBack()}>
          <ArrowLeft className="mr-1.5 size-4" /> Back to workflows
        </Button>
      </div>
    );
  }

  const disabled = !canEdit;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void goBack()}
          className="-ml-2"
        >
          <ArrowLeft className="mr-1.5 size-4" /> Workflows
        </Button>
        {canEdit && (
          <AutosaveStatus
            status={status}
            lastSavedAt={autosave.lastSavedAt}
            onRetry={autosave.retry}
          />
        )}
      </div>

      {/* Settings card */}
      <div className="space-y-4 rounded-xl border border-border bg-background p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="tmpl-name">Name</Label>
            <Input
              id="tmpl-name"
              value={header.name}
              disabled={disabled}
              onChange={(e) =>
                setHeader((h) => h && { ...h, name: e.target.value })
              }
              onBlur={() => void flush()}
              placeholder="e.g. CVL — first 30 days"
            />
          </div>

          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="tmpl-desc">Description</Label>
            <Textarea
              id="tmpl-desc"
              value={header.description}
              disabled={disabled}
              onChange={(e) =>
                setHeader((h) => h && { ...h, description: e.target.value })
              }
              onBlur={() => void flush()}
              placeholder="What this workflow is for…"
              className="min-h-[60px]"
            />
          </div>

          <div className="space-y-1">
            <Label>Apply mode</Label>
            <Select
              value={header.applyMode}
              onValueChange={(v) =>
                setHeader((h) => h && { ...h, applyMode: v as ApplyMode })
              }
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APPLY_MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Date anchor</Label>
            <Select
              value={header.anchor}
              onValueChange={(v) =>
                setHeader((h) => h && { ...h, anchor: v as Anchor })
              }
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANCHOR_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {header.applyMode === "by_type" && (
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label>Appointment types</Label>
              <MultiSearchableSelect
                value={header.appointmentTypes}
                onValueChange={(v) =>
                  setHeader((h) => h && { ...h, appointmentTypes: v })
                }
                options={APPOINTMENT_TYPE_TAGS.map((t) => ({
                  value: t.value,
                  label: t.label,
                }))}
                placeholder="Choose appointment types"
                searchPlaceholder="Search types…"
              />
              <p className="text-[11px] text-muted-foreground">
                This workflow auto-applies when a job of any selected type is
                created.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tasks ⇄ Automations tabs (MOR-736). Automations are part of the
          workflow but live on their own tab so a long task list doesn't
          push them off the bottom of the page. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as EditorTab)}
        >
          <TabsList>
            <TabsTrigger value="tasks">Tasks ({items.length})</TabsTrigger>
            <TabsTrigger value="automations">
              <Zap className="mr-1.5 size-3.5" />
              Workflow automations ({templateRules?.length ?? 0})
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {activeTab === "tasks" && (
          <div className="flex items-center gap-2">
            {/* List ⇄ visual builder toggle (MOR-736) */}
            <div className="flex items-center rounded-md border border-border p-0.5">
              <Button
                type="button"
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs"
                onClick={() => setViewMode("list")}
              >
                <List className="size-3.5" />
                List
              </Button>
              <Button
                type="button"
                variant={viewMode === "flow" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs"
                onClick={() => setViewMode("flow")}
              >
                <Workflow className="size-3.5" />
                Visual
              </Button>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={addItem}>
                <Plus className="mr-1.5 size-4" /> Add task
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Automations tab — the rules built into this workflow. */}
      {activeTab === "automations" && (
        <WorkflowAutomationsSection
          orgId={orgId}
          templateId={templateId}
          templateName={header?.name ?? "this workflow"}
          taskTitles={items.map((it) => it.title)}
          canEdit={canEdit}
        />
      )}

      {/* Items */}
      <div className={cn("space-y-3", activeTab !== "tasks" && "hidden")}>
        {/* Visual graph view (MOR-736) — same draft items, autosaves too. */}
        {viewMode === "flow" &&
          (items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
              No tasks yet.
              {canEdit && " Add the first task to build this workflow."}
            </div>
          ) : (
            <WorkflowFlowView
              orgId={orgId}
              templateId={templateId}
              templateName={header.name}
              items={items}
              statuses={statusOptions}
              labels={labelOptions}
              canEdit={!disabled}
              onPatchItem={patchItem}
              onRemoveItem={removeItem}
            />
          ))}

        {/* Search + filters (only worth showing once there are a few tasks) */}
        {viewMode === "list" && items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tasks…"
                className="h-9 pl-8"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ALL}>All statuses</SelectItem>
                <SelectItem value={FILTER_NO_STATUS}>No status</SelectItem>
                {statusOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ALL}>All priorities</SelectItem>
                {TASK_PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={String(p.value)}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {canEdit && (
              <Button
                type="button"
                variant={selectionMode ? "default" : "outline"}
                size="sm"
                className="h-9 gap-1.5 text-xs"
                onClick={selectionMode ? exitSelectionMode : enterSelectionMode}
              >
                <CheckSquare className="size-3.5" />
                {selectionMode ? "Deselect All" : "Select All"}
              </Button>
            )}

            {isFiltering && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-9 text-muted-foreground"
              >
                <X className="mr-1 size-4" /> Clear
              </Button>
            )}
          </div>
        )}

        {viewMode === "list" && isFiltering && items.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Showing {visibleItems.length} of {items.length} tasks.
          </p>
        )}

        {viewMode !== "list" ? null : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
            No tasks yet.
            {canEdit && " Add the first task to build this workflow."}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
            No tasks match your search or filters.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleReorder}
          >
            <SortableContext
              items={visibleItems.map(({ it }) => it.localKey)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {visibleItems.map(({ it, idx }) => (
                  <SortableTemplateItem
                    key={it.localKey}
                    orgId={orgId}
                    item={it}
                    index={idx}
                    depth={depthByKey.get(it.localKey) ?? 0}
                    statuses={statusOptions}
                    labels={labelOptions}
                    otherItems={otherItems}
                    disabled={disabled}
                    // Reordering only when the full, unfiltered list is shown.
                    canDrag={!disabled && !isFiltering}
                    selected={selectedKeys.has(it.localKey)}
                    onSelectedChange={
                      canEdit
                        ? (sel) => toggleSelected(it.localKey, sel)
                        : undefined
                    }
                    onChange={(patch) => patchItem(it.localKey, patch)}
                    onRemove={() => removeItem(it.localKey)}
                    onAddSubtask={
                      canEdit ? () => addSubtask(it.localKey) : undefined
                    }
                    autoFocusTitle={focusKey === it.localKey}
                    automationRules={automationRulesByKey.get(it.localKey)}
                    templateId={templateId}
                    templateName={header.name}
                    workflowTaskTitles={workflowTaskTitles}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Second "Add task" at the foot of the list so a long workflow doesn't
            have to be scrolled back to the top to append a task. */}
        {viewMode === "list" && canEdit && items.length > 0 && (
          <button
            type="button"
            onClick={addItem}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border bg-background/40 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          >
            <Plus className="size-4" />
            Add task
          </button>
        )}
      </div>

      {/* Floating action bar — fixed to the bottom of the viewport while in
          selection mode (same pattern as the precedent templates gallery). */}
      {canEdit && (selectionMode || selectedKeys.size > 0) && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-primary-foreground/20 bg-primary px-4 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.12)]">
            <span className="text-sm font-medium text-primary-foreground">
              {selectedKeys.size} selected
            </span>
            <div className="h-4 w-px bg-primary-foreground/20" />
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-white text-xs text-primary hover:bg-white/90"
              disabled={selectedKeys.size === 0}
              onClick={() => setBulkEditOpen(true)}
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-white text-xs text-primary hover:bg-white/90"
              disabled={selectedKeys.size === 0}
              onClick={bulkDuplicate}
            >
              <Copy className="size-3.5" />
              Duplicate
            </Button>
            <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 bg-white text-xs text-red-500 hover:bg-white/90 hover:text-red-600"
                  disabled={selectedKeys.size === 0}
                >
                  <Trash2 className="size-3.5" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Delete {selectedKeys.size} task
                    {selectedKeys.size === 1 ? "" : "s"}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    The selected tasks are removed from this workflow. Tasks
                    already applied to jobs are not affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={bulkDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete {selectedKeys.size} task
                    {selectedKeys.size === 1 ? "" : "s"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              onClick={exitSelectionMode}
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {canEdit && (
        <WorkflowBulkEditDialog
          open={bulkEditOpen}
          onOpenChange={setBulkEditOpen}
          orgId={orgId}
          count={selectedKeys.size}
          statuses={statusOptions}
          labels={labelOptions}
          items={items}
          selectedKeys={selectedKeys}
          onApply={applyBulkEdit}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk Edit Dialog — apply field changes to many workflow tasks at once.
// Mirrors the precedent templates bulk-edit dialog: each field is gated by a
// checkbox so only the ticked dimensions are applied.
// ---------------------------------------------------------------------------

/** One checkbox-gated field section of the bulk-edit dialog. */
function BulkField({
  icon,
  title,
  enabled,
  onEnabledChange,
  children,
}: {
  icon: ReactNode;
  title: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 px-4 py-3",
        !enabled && "bg-secondary/20",
      )}
    >
      <label className="flex cursor-pointer items-center gap-2">
        <Checkbox
          checked={enabled}
          onCheckedChange={(v) => onEnabledChange(v === true)}
        />
        <span className="flex items-center gap-1.5 text-sm font-medium text-primary/80">
          {icon}
          {title}
        </span>
      </label>
      {enabled && <div className="mt-3">{children}</div>}
    </div>
  );
}

function WorkflowBulkEditDialog({
  open,
  onOpenChange,
  orgId,
  count,
  statuses,
  labels,
  items,
  selectedKeys,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: Id<"organizations">;
  count: number;
  statuses: ReadonlyArray<{ key: string; name: string }>;
  labels: ReadonlyArray<{ key: string; name: string }>;
  /** Every task in the workflow — the candidate pool for parent / blocker links. */
  items: ReadonlyArray<DraftItem>;
  selectedKeys: ReadonlySet<string>;
  onApply: (change: BulkEditChange) => void;
}) {
  const [updateStatus, setUpdateStatus] = useState(false);
  const [statusValue, setStatusValue] = useState("");
  const [updatePriority, setUpdatePriority] = useState(false);
  const [priorityValue, setPriorityValue] = useState("");
  const [updateAssignee, setUpdateAssignee] = useState(false);
  const [assigneeValue, setAssigneeValue] = useState<MemberOrRoleValue>({
    userId: null,
    roleId: null,
  });
  const [updateAssignor, setUpdateAssignor] = useState(false);
  const [assignorValue, setAssignorValue] = useState<MemberOrRoleValue>({
    userId: null,
    roleId: null,
  });
  const [updateLabels, setUpdateLabels] = useState(false);
  const [labelValues, setLabelValues] = useState<string[]>([]);
  const [updateParent, setUpdateParent] = useState(false);
  const [parentValue, setParentValue] = useState("");
  const [updateBlockedBy, setUpdateBlockedBy] = useState(false);
  const [blockedByMode, setBlockedByMode] = useState<"replace" | "add">(
    "replace",
  );
  const [blockedByValues, setBlockedByValues] = useState<string[]>([]);
  const [updateBlocking, setUpdateBlocking] = useState(false);
  const [blockingValues, setBlockingValues] = useState<string[]>([]);
  const [updateDue, setUpdateDue] = useState(false);
  const [dueDraft, setDueDraft] = useState("");

  // Start clean on every open.
  useEffect(() => {
    if (!open) {
      setUpdateStatus(false);
      setStatusValue("");
      setUpdatePriority(false);
      setPriorityValue("");
      setUpdateAssignee(false);
      setAssigneeValue({ userId: null, roleId: null });
      setUpdateAssignor(false);
      setAssignorValue({ userId: null, roleId: null });
      setUpdateLabels(false);
      setLabelValues([]);
      setUpdateParent(false);
      setParentValue("");
      setUpdateBlockedBy(false);
      setBlockedByMode("replace");
      setBlockedByValues([]);
      setUpdateBlocking(false);
      setBlockingValues([]);
      setUpdateDue(false);
      setDueDraft("");
    }
  }, [open]);

  const taskLabel = (it: DraftItem) => it.title.trim() || "(untitled task)";

  /**
   * Tasks outside the selection. Used for both dependency fields: a task can't
   * block itself, and offering the selection back would let one apply create a
   * mutual block between two selected rows.
   */
  const linkOptions = useMemo(
    () =>
      items
        .filter((it) => !selectedKeys.has(it.localKey))
        .map((it) => ({ value: it.localKey, label: taskLabel(it) })),
    [items, selectedKeys],
  );

  /**
   * Valid parents: everything except the selection itself (a self-parent) and
   * anything beneath it, since re-parenting a task under its own descendant
   * closes a parent loop that `replaceItems` rejects.
   */
  const parentOptions = useMemo(() => {
    const excluded = invalidParentKeys(items, selectedKeys);
    return items
      .filter((it) => !excluded.has(it.localKey))
      .map((it) => ({ value: it.localKey, label: taskLabel(it) }));
  }, [items, selectedKeys]);

  const hasAnyChange =
    (updateStatus && statusValue !== "") ||
    (updatePriority && priorityValue !== "") ||
    updateAssignee ||
    updateAssignor ||
    updateLabels ||
    (updateParent && parentValue !== "") ||
    // "Replace" with nothing picked is a deliberate clear; "add" with nothing
    // picked is a no-op.
    (updateBlockedBy &&
      (blockedByMode === "replace" || blockedByValues.length > 0)) ||
    (updateBlocking && blockingValues.length > 0) ||
    updateDue;

  const handleApply = () => {
    if (count === 0 || !hasAnyChange) return;
    const patch: Partial<DraftItem> = {};
    if (updateStatus && statusValue !== "") {
      patch.statusKey = statusValue === BULK_NONE ? null : statusValue;
    }
    if (updatePriority && priorityValue !== "") {
      const n = Number(priorityValue);
      patch.priority = n === 0 ? null : n;
    }
    if (updateAssignee) {
      patch.assigneeUserId = assigneeValue.userId;
      patch.assigneeRoleId = assigneeValue.roleId;
    }
    if (updateAssignor) {
      patch.assignorUserId = assignorValue.userId;
      patch.assignorRoleId = assignorValue.roleId;
    }
    if (updateLabels) patch.labelKeys = labelValues;
    if (updateParent && parentValue !== "") {
      patch.parentLocalKey = parentValue === BULK_NONE ? null : parentValue;
    }
    if (updateDue) {
      const t = dueDraft.trim();
      const n = Number(t);
      patch.dueOffsetDays = t && Number.isFinite(n) ? Math.round(n) : null;
    }

    const change: BulkEditChange = { patch };
    if (
      updateBlockedBy &&
      (blockedByMode === "replace" || blockedByValues.length > 0)
    ) {
      change.blockedBy = { mode: blockedByMode, keys: blockedByValues };
    }
    if (updateBlocking && blockingValues.length > 0) {
      change.blockingKeys = blockingValues;
    }
    onApply(change);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Edit {count} task{count === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>Select what element to update.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <BulkField
            icon={<CircleDot className="size-3.5" />}
            title="Status"
            enabled={updateStatus}
            onEnabledChange={setUpdateStatus}
          >
            <Select value={statusValue} onValueChange={setStatusValue}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Pick a status…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BULK_NONE}>Default (To Do)</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </BulkField>

          <BulkField
            icon={<Flag className="size-3.5" />}
            title="Priority"
            enabled={updatePriority}
            onEnabledChange={setUpdatePriority}
          >
            <Select value={priorityValue} onValueChange={setPriorityValue}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Pick a priority…" />
              </SelectTrigger>
              <SelectContent>
                {TASK_PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={String(p.value)}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </BulkField>

          <BulkField
            icon={<User className="size-3.5" />}
            title="Assignee"
            enabled={updateAssignee}
            onEnabledChange={setUpdateAssignee}
          >
            <MemberOrRoleSelect
              orgId={orgId}
              value={assigneeValue}
              onChange={setAssigneeValue}
              placeholder="Unassigned"
            />
          </BulkField>

          <BulkField
            icon={<UserCheck className="size-3.5" />}
            title="Assigned by"
            enabled={updateAssignor}
            onEnabledChange={setUpdateAssignor}
          >
            <MemberOrRoleSelect
              orgId={orgId}
              value={assignorValue}
              onChange={setAssignorValue}
              placeholder="Nobody"
            />
          </BulkField>

          {labels.length > 0 && (
            <BulkField
              icon={<Tags className="size-3.5" />}
              title="Labels"
              enabled={updateLabels}
              onEnabledChange={setUpdateLabels}
            >
              <div className="space-y-2">
                <MultiSearchableSelect
                  value={labelValues}
                  onValueChange={setLabelValues}
                  options={labels.map((l) => ({
                    value: l.key,
                    label: l.name,
                  }))}
                  placeholder="No labels"
                  searchPlaceholder="Search labels…"
                  className="h-9 w-full"
                />
                <p className="text-[11px] text-muted-foreground">
                  Replaces the existing labels on every selected task.
                </p>
              </div>
            </BulkField>
          )}

          {parentOptions.length > 0 && (
            <BulkField
              icon={<CornerDownRight className="size-3.5" />}
              title="Subtask of"
              enabled={updateParent}
              onEnabledChange={setUpdateParent}
            >
              <div className="space-y-2">
                <SearchableSelect
                  value={parentValue}
                  onValueChange={setParentValue}
                  options={parentOptions}
                  allOptionLabel="Top-level task"
                  allOptionValue={BULK_NONE}
                  placeholder="Pick a parent task…"
                  searchPlaceholder="Search tasks…"
                  className="h-9 w-full"
                  contentClassName="w-[420px]"
                />
                <p className="text-[11px] text-muted-foreground">
                  Makes every selected task a subtask of this one. The selected
                  tasks and their own subtasks aren't listed, so this can't
                  create a loop. Pick “Top-level task” to detach them.
                </p>
              </div>
            </BulkField>
          )}

          {linkOptions.length > 0 && (
            <BulkField
              icon={<Ban className="size-3.5" />}
              title="Blocked by"
              enabled={updateBlockedBy}
              onEnabledChange={setUpdateBlockedBy}
            >
              <div className="space-y-2">
                <Select
                  value={blockedByMode}
                  onValueChange={(v) =>
                    setBlockedByMode(v as "replace" | "add")
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="replace">Replace existing</SelectItem>
                    <SelectItem value="add">Add to existing</SelectItem>
                  </SelectContent>
                </Select>
                <MultiSearchableSelect
                  value={blockedByValues}
                  onValueChange={setBlockedByValues}
                  options={linkOptions}
                  placeholder="Nothing"
                  searchPlaceholder="Search tasks…"
                  className="h-9 w-full"
                  contentClassName="w-[420px]"
                />
                <p className="text-[11px] text-muted-foreground">
                  {blockedByMode === "replace"
                    ? "Replaces the blockers on every selected task. Leave empty to clear them."
                    : "Keeps the blockers each selected task already has and adds these."}
                </p>
              </div>
            </BulkField>
          )}

          {linkOptions.length > 0 && (
            <BulkField
              icon={<Link2 className="size-3.5" />}
              title="Blocks"
              enabled={updateBlocking}
              onEnabledChange={setUpdateBlocking}
            >
              <div className="space-y-2">
                <MultiSearchableSelect
                  value={blockingValues}
                  onValueChange={setBlockingValues}
                  options={linkOptions}
                  placeholder="Nothing"
                  searchPlaceholder="Search tasks…"
                  className="h-9 w-full"
                  contentClassName="w-[420px]"
                />
                <p className="text-[11px] text-muted-foreground">
                  Adds the selected tasks as blockers on each task picked here,
                  alongside any blockers those tasks already have.
                </p>
              </div>
            </BulkField>
          )}

          <BulkField
            icon={<CalendarClock className="size-3.5" />}
            title="Due offset (days)"
            enabled={updateDue}
            onEnabledChange={setUpdateDue}
          >
            <div className="space-y-2">
              <Input
                type="text"
                inputMode="numeric"
                value={dueDraft}
                onChange={(e) => setDueDraft(e.target.value)}
                placeholder="e.g. 5"
                className="h-9"
              />
              <p className="text-[11px] text-muted-foreground">
                Days after the anchor date. Leave empty to clear the due offset
                on every selected task.
              </p>
            </div>
          </BulkField>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!hasAnyChange || count === 0}>
            Apply to {count} task{count === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
